import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';

// Panel "Wątek (Customer Context Store)" — montowany wewnątrz profilu kontaktu.
// Pokazuje timeline wiadomości z customer_threads, tagi, notatki, akcje (dodaj tag/notatkę).

export default function CustomerThreadPanel({ contactId }) {
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [newTag, setNewTag] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!contactId) return;
    setLoading(true);
    try {
      const r = await api.get(`/customers/${contactId}/thread`, { params: { limit_messages: 100 } });
      setThread(r.data);
    } catch (e) {
      setThread({ exists: false, error: e.response?.data?.detail || 'Brak threada' });
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => { load(); }, [load]);

  const addNote = async () => {
    if (!newNote.trim() || saving) return;
    setSaving(true);
    try {
      await api.post(`/customers/${contactId}/note`, { note: newNote.trim() });
      setNewNote('');
      await load();
    } finally { setSaving(false); }
  };

  const addTag = async () => {
    if (!newTag.trim() || saving) return;
    setSaving(true);
    try {
      await api.post(`/customers/${contactId}/tag`, { tag: newTag.trim() });
      setNewTag('');
      await load();
    } finally { setSaving(false); }
  };

  const removeTag = async (tag) => {
    setSaving(true);
    try {
      await api.delete(`/customers/${contactId}/tag/${encodeURIComponent(tag)}`);
      await load();
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div style={{ padding: 12, opacity: 0.7, fontSize: 13 }}>Ładuję wątek…</div>;
  }
  if (!thread || thread.exists === false) {
    return (
      <div style={{ padding: 12, opacity: 0.7, fontSize: 13, border: '1px dashed hsl(var(--border))', borderRadius: 6 }}>
        Brak wątku w Customer Context Store dla tego kontaktu (jeszcze nie miał interakcji lub sync nie dotarł).
      </div>
    );
  }

  const messages = thread.messages || [];
  const tags = thread.tags || [];
  const notes = thread.agent_notes || [];
  const offers = thread.offers_history || [];
  const corrections = thread.data_corrections || [];

  return (
    <div className="space-y-3">
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12 }}>
        <Stat label="Wiadomości" value={thread.message_count || messages.length} />
        <Stat label="Tagi" value={tags.length} />
        <Stat label="Notatki" value={notes.length} />
        <Stat label="Oferty" value={offers.length} />
        <Stat label="Korekty danych" value={corrections.length} />
        <Stat label="Kanały" value={(thread.channels || []).join(', ') || '-'} />
        {thread.intent_signals?.last_intent && (
          <Stat label="Ostatnia intencja" value={thread.intent_signals.last_intent} />
        )}
      </div>

      {/* TAGI */}
      <div>
        <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Tagi</p>
        <div className="flex flex-wrap gap-1.5 items-center">
          {tags.map(t => (
            <span key={t} style={tagStyle} onClick={() => removeTag(t)} title="Klik = usuń">
              {t} <span style={{ opacity: 0.5, marginLeft: 4 }}>×</span>
            </span>
          ))}
          <input
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addTag()}
            placeholder="+ nowy tag"
            style={tagInputStyle}
          />
        </div>
      </div>

      {/* TIMELINE */}
      <div>
        <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">
          Timeline ({messages.length}{thread.messages_truncated ? ` z ${thread.messages_total}` : ''})
        </p>
        {messages.length === 0 ? (
          <div style={{ fontSize: 12, opacity: 0.6 }}>Brak wiadomości w wątku.</div>
        ) : (
          <div style={timelineStyle}>
            {messages.slice().reverse().map(m => (
              <div key={m.id} style={{ ...messageRow, ...(m.direction === 'outgoing' ? msgOut : msgIn) }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontSize: 10, opacity: 0.7 }}>
                    {m.channel}/{m.direction}
                    {m.ai_generated && ' • AI'}
                    {m.campaign_id && ' • camp'}
                  </span>
                  <span style={{ fontSize: 10, opacity: 0.6 }}>{m.ts?.replace('T', ' ').slice(0, 19)}</span>
                </div>
                <div style={{ fontSize: 12, marginTop: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.content}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* NOTATKI */}
      <div>
        <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Notatki agenta ({notes.length})</p>
        {notes.slice().reverse().slice(0, 5).map((n, i) => (
          <div key={i} style={noteStyle}>
            <div style={{ fontSize: 10, opacity: 0.6, marginBottom: 2 }}>
              {n.ts?.replace('T', ' ').slice(0, 19)} • {n.author}{n.category && ` • ${n.category}`}
            </div>
            <div style={{ fontSize: 12 }}>{n.note}</div>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <input
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addNote()}
            placeholder="Nowa notatka..."
            style={noteInputStyle}
          />
          <button onClick={addNote} disabled={saving || !newNote.trim()} style={btnPrim}>Dodaj</button>
        </div>
      </div>

      {/* OFERTY */}
      {offers.length > 0 && (
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Oferty wysłane ({offers.length})</p>
          {offers.slice().reverse().slice(0, 10).map((o, i) => (
            <div key={i} style={{ fontSize: 11, padding: '4px 0', borderBottom: '1px solid hsl(var(--border) / 0.3)' }}>
              {o.sent_at?.slice(0, 19)} • {o.channel} • offer={o.offer_id?.slice(0, 8)} • {o.status} → {o.conversion_value || 0} zł
            </div>
          ))}
        </div>
      )}

      {/* KOREKTY */}
      {corrections.length > 0 && (
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Korekty danych ({corrections.length})</p>
          {corrections.slice().reverse().slice(0, 5).map((c, i) => (
            <div key={i} style={{ fontSize: 11, padding: '4px 0' }}>
              {c.ts?.slice(0, 19)} • <b>{c.field}</b>: {String(c.old_value)} → {String(c.new_value)} <span style={{ opacity: 0.6 }}>({c.source})</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div style={{ background: 'hsl(var(--muted) / 0.3)', padding: '4px 10px', borderRadius: 4, border: '1px solid hsl(var(--border) / 0.3)' }}>
    <div style={{ opacity: 0.6, fontSize: 10 }}>{label}</div>
    <div style={{ fontWeight: 600 }}>{value}</div>
  </div>
);

const tagStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  fontSize: 11,
  padding: '2px 8px',
  borderRadius: 12,
  background: '#3a2a5f',
  color: '#fff',
  cursor: 'pointer',
};

const tagInputStyle = {
  fontSize: 11,
  padding: '2px 8px',
  borderRadius: 12,
  background: 'transparent',
  border: '1px dashed hsl(var(--border))',
  outline: 'none',
  width: 100,
};

const timelineStyle = {
  maxHeight: 320,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  padding: 6,
  background: 'hsl(var(--muted) / 0.15)',
  borderRadius: 6,
};

const messageRow = { padding: '6px 10px', borderRadius: 6, maxWidth: '95%' };
const msgIn = { background: 'hsl(var(--muted) / 0.5)', alignSelf: 'flex-start' };
const msgOut = { background: 'hsl(217 91% 60% / 0.15)', borderLeft: '2px solid hsl(217 91% 60%)', alignSelf: 'flex-end' };

const noteStyle = { padding: 6, marginBottom: 4, background: 'hsl(var(--muted) / 0.3)', borderRadius: 4, borderLeft: '2px solid #7c3aed' };
const noteInputStyle = { flex: 1, fontSize: 12, padding: '4px 8px', borderRadius: 4, background: 'hsl(var(--background))', border: '1px solid hsl(var(--border))', outline: 'none' };
const btnPrim = { padding: '4px 12px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600 };
